import { FormGroup, FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { IssueModel } from '@fleet/model';

export function disableForm(form: FormGroup) {
  Object.keys(form.controls).forEach((controlName) => {
    form.controls[controlName].disable(); // disables/enables each form control based on 'this.formDisabled'
  });
}

export function enableForm(form: FormGroup) {
  Object.keys(form.controls).forEach((controlName) => {
    form.controls[controlName].enable(); // disables/enables each form control based on 'this.formDisabled'
  });
}

export class TouchedDirtyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    form: FormGroupDirective | NgForm | null
  ): boolean {
    return !!(control && control.invalid && control.dirty && control.touched);
  }
}

/**
 * Maps API IssueModel errors to form control errors
 * @param form The FormGroup to set errors on
 * @param issues Array of IssueModel from API response
 */
export function setApiErrors(form: FormGroup, issues: IssueModel[]): void {
  // Clear existing API errors first
  clearApiErrors(form);

  // Map each issue to its field(s)
  issues?.forEach((issue) => {
    issue.fields?.forEach((fieldName) => {
      const control = form.get(fieldName);
      if (control) {
        // Preserve existing errors and add apiError
        const currentErrors = control.errors || {};
        control.setErrors({
          ...currentErrors,
          apiError: { message: issue.message },
        });
        control.markAsTouched(); // Ensure mat-error displays
      }
    });
  });
}

/**
 * Clears API errors from all form controls
 * @param form The FormGroup to clear API errors from
 */
export function clearApiErrors(form: FormGroup): void {
  Object.keys(form.controls).forEach((key) => {
    const control = form.get(key);
    if (control?.hasError('apiError') && control.errors) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { apiError, ...remainingErrors } = control.errors;
      control.setErrors(
        Object.keys(remainingErrors).length > 0 ? remainingErrors : null
      );
    }
  });
}

/**
 * Filters issues to only return non-field-related errors
 * Use this to show general errors in alerts while field-specific errors are displayed on form controls
 * @param issues Array of IssueModel from API response
 * @returns Array of issues that don't have associated field names
 */
export function getNonFieldRelatedIssues(issues: IssueModel[]): IssueModel[] {
  return issues?.filter(issue => !issue.fields || issue.fields.length === 0) || [];
}
